import React from 'react';
import pick from 'lodash/pick';
import idx from 'idx';
import { LoadingIndicator } from 'common/components/preloader';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import { Form, Field } from 'redux-form';
import { ResizableTextAreaField } from 'common/components/form_fields';
import { getFieldPropsFromDefinition, getFeatureProjectFields } from 'pap/utils';


export default class PAPEditReviewSection extends React.Component {
  constructor(props) {
    super(props)

    this.state = {stepIdx: 0}
    this.prev = this.prev.bind(this)
    this.next = this.next.bind(this)
    this.closeEditor = this.closeEditor.bind(this)
    this.handleSubmitError = this.handleSubmitError.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleBriefUpdates = this.handleBriefUpdates.bind(this)
    this.handleCostUpdates = this.handleCostUpdates.bind(this)
    this.handleContractUpdates = this.handleContractUpdates.bind(this)
    this.handleAttachmentsUpdates = this.handleAttachmentsUpdates.bind(this)
    this.handle1to1BudgetUpdate = this.handle1to1BudgetUpdate.bind(this)
    this.handleCancel = this.handleCancel.bind(this);
  }

  componentDidMount() {
    this.props.fetchCountriesIfNeeded()
  }

  prev(e) {
    e.preventDefault()
    this.setState({stepIdx: this.state.stepIdx - 1})
  }

  next(e) {
    e.preventDefault()
    this.setState({stepIdx: this.state.stepIdx + 1})
  }

  handleCancel(e) {
    e.preventDefault();
    this.props.cancelEditSection();
  }

  closeEditor() {
    const { fetchProject, fetchCustomContracts, fetchAttachments, projectID, cancelEditSection,
            fetchMessagesIfNeeded, isProjectPage, projectData } = this.props

    /* reload project with eventual updates */
    fetchProject(this.props.projectID)
    /* fetch custom contract PDFs in case they've changed */
    fetchCustomContracts(this.props.projectID)
    /* fetch attachments in case they've changed */
    fetchAttachments(this.props.projectID)
    /* if on project page, fetch updated list of messages */
    if (isProjectPage && projectData['message_stream_id']) {
      fetchMessagesIfNeeded({
        streamId: projectData['message_stream_id'],
        force: true,
        project: projectData
      })
    }

    cancelEditSection()
  }

  handleSubmitError(response) {
    const { setBannerError, sectionData } = this.props;

    if (sectionData.name == "file_upload" && idx(response, _ => _.data.filename[0])) {
      setBannerError('Invalid attachment', idx(response, _ => _.data.filename[0]));
      return;
    }

    setBannerError('Error updating project', 'Please try again.');
  }

  handleBriefUpdates(data) {
    const {
      updateProjectRequest, updateBriefRequest, projectID, briefAnswers, isProjectPage,
      isAdmin, adminUpdateBrief,
    } = this.props;

    /* if on the project page, it means the project can't be updated directly,
       but only via brief updates */
    if (isProjectPage) {
      return updateBriefRequest(projectID, data);
    }

    /* if user is an admin, use special request instead */
    if (isAdmin) {
      return adminUpdateBrief(projectID, data);
    }

    const reqData = { brief_answers: { ...briefAnswers, ...data } };
    return updateProjectRequest(projectID, reqData);
  }

  handle1to1BudgetUpdate(data) {
    const { projectData, projectID, awardValue1to1, updateProjectRequest } = this.props

    /* project is not 1-1: nothing to do */
    if (!projectData.is_one_to_one) {
      return Promise.resolve()
    }

    const budgetData = data['budget'] || {}
    return updateProjectRequest(projectID, {
      one_to_one_negotiation_value: (
        budgetData['budget_option'] == 'request_quote'? 0 : awardValue1to1
      )
    })
  }

  handleCostUpdates(data) {
    const {
      updateProjectRequest, updateFeaturesRequest, updateAwardsRequest, projectID,
      selectedPackage, projectData, user
    } = this.props

    const hasPackageChanged = selectedPackage.id != projectData.original_package;
    const projectUpdates = {
      /* new package selected: update project before performing other updates */
      original_package: hasPackageChanged? selectedPackage.id : undefined,
      /* project fields that can be changed once features are activated */
      ...getFeatureProjectFields(data, projectData),
    };

    return updateProjectRequest(projectID, projectUpdates).then((res) => {
      return updateAwardsRequest(projectID, res.data.award_rules, data).then(() => {
        return updateFeaturesRequest(
          projectID, res.data.features, data, res.data.billables, user.profile_data
        ).then(() => {
          return this.handle1to1BudgetUpdate(data);
        });
      });
    });
  }

  handleContractUpdates(data) {
    const { updateProjectRequest, projectID, customContract, createCustomContractRequest,
            deleteCustomContractRequest } = this.props

    const contractData = data['contract']

    /* upload custom contract if provided (replacing current one if it exists) ... */
    if (contractData.isCustom) {
      /* custom contract hasn't changed */
      if (contractData.custom_contract.id) {
        return Promise.resolve();
      }

      return createCustomContractRequest(projectID, contractData.custom_contract)
    }

    /* or update project with information that will be used to create a contract
       out of CS templates, deleting the custom contract if it exists */
    return updateProjectRequest(
      projectID, pick(contractData, ['client_legal_name', 'client_legal_country'])
    ).then(() => {
      if (customContract) {
        return deleteCustomContractRequest(projectID, customContract.id)
      }
    })
  }

  handleAttachmentsUpdates(data) {
    const { projectID, attachments, updateAttachments } = this.props
    return updateAttachments(projectID, attachments, data)
  }

  handleSubmit(data) {
    const { sectionData, updateProjectRequest, projectID } = this.props
    let promise = null

    /* "files" brief section: update attachments */
    if (sectionData.name == "file_upload") {
      promise = this.handleAttachmentsUpdates(data)

    /* all other sections for the "brief" group */
    } else if (this.props.type == "brief") {
      promise = this.handleBriefUpdates(data)

    /* "cost" section: update package and its options */
    } else if (sectionData.name == "cost") {
      promise = this.handleCostUpdates(data)

    /* "contract" section: save ICA contract preferences */
    } else if (sectionData.name == "contract") {
      promise = this.handleContractUpdates(data)

    /* remaining sections of "details" group */
    } else {
      promise = updateProjectRequest(projectID, data)
    }

    return promise.then(this.closeEditor)
      .catch(this.handleSubmitError);
  }

  render () {
    const {
      submitting, sectionData, projectData, selectedPackage, countries, valid,
      subCategory, isAdmin, type, user
    } = this.props;
    const { stepIdx } = this.state;

    if (submitting) {
      return <div className="l-content"><LoadingIndicator /></div>;
    }

    /* each section being edited is made of at least 1 PAP step */
    const step = sectionData.steps[stepIdx]

    const hasPackageChanged = selectedPackage && selectedPackage.id != projectData.original_package;
    const fieldProps = getFieldPropsFromDefinition(step, {
      user: user.profile_data,
      displayFullScreen: this.props.displayFullScreen,
      hideFullScreen: this.props.hideFullScreen,
      fullScreen: this.props.workflow.fullScreen,
      packages: this.props.packages,
      currentFeatures: hasPackageChanged ? selectedPackage.features: projectData.features,
      currentAwards: hasPackageChanged ? selectedPackage.awards : projectData.award_rules,
      currentTips: hasPackageChanged ? selectedPackage.package_tips : projectData.package_tips,
      projectData: projectData,
      countries: countries,
      simplified: true,
      subCategory
    });

    const canAddNotes = isAdmin && type === 'brief' && sectionData.name !== 'file_upload';

    return (
      <div className={`l-content pap-review-editor ${type}`}>
        <Form onSubmit={this.props.handleSubmit(this.handleSubmit)}>
          <div className="pap-form">
            <PAPQuestionBase {...fieldProps} data-transition-id={`brief-${fieldProps.name}`}/>
          </div>
          <div className="editing-nav">
            <div className="editing-status">
              <div className="row">
                <div className="columns">
                  <h6>Editing</h6>
                </div>
              </div>
            </div>
            <div className="editing-controls">
              <div className="row">
                {!isAdmin && <div className="columns small-6 large-5 left">
                  {stepIdx > 0 &&
                  <button onClick={this.prev} className="btn btn-back btn-text">
                    <i className="fa fa-angle-left"></i> <span className="button-label">Back</span>
                  </button>}
                </div>}
                {canAddNotes && <div className="columns small-16 large-11">
                  <label>Admin Note</label>
                  <Field
                    name="project_admin_note"
                    component={ResizableTextAreaField}
                  />
                </div>}
                <div className="columns small-10 large-offset-6 large-5 right">
                  <button onClick={this.handleCancel} className="btn btn-text">
                    Cancel
                  </button>

                  {stepIdx == sectionData.steps.length - 1?
                  <button type="submit" className="btn btn-primary" disabled={!valid}>
                    Done
                  </button>:
                  <button onClick={this.next} className="btn btn-primary" disabled={!valid}>
                    Next
                  </button>}
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>)
  }
}



// WEBPACK FOOTER //
// ./pap/components/review_editor.jsx